import type { AiCall, AiCallDetail } from "@pax/api-contract/ai/calls/v1alpha1";
import {
  jsonObjects,
  ModelTransport,
  type PromptPart,
  readModelChunks,
  readModelPrompt,
  readModelReply,
  sharedPromptPrefix,
} from "@pax/api-contract/ai/shared";
import type { GameSpan } from "@pax/api-contract/traces/v1alpha1";
import { GEN_AI } from "@pax/telemetry/attributes";
import { EdgePanel, focusHaloFlush, panelMotion } from "@pax/ui";
import { X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Timestamp } from "@/components/Humane";
import { markButton } from "@/features/evals/Bubbles";
import {
  Disclosure,
  Failure,
  formatCount,
  formatDuration,
  formatUsd,
  link,
  plural,
  ReadableValue,
} from "@/features/evals/components";
import {
  type Activity,
  firstSeenIn,
  modelCallBefore,
  revisionList,
  spanEndMs,
  toolCallIdOf,
  toolNameOf,
  toolResultOf,
  toolSpansAfter,
} from "./activities";
import { CallTimeline } from "./CallTimeline";
import { CopyableId, CopyButton } from "./copy";
import { useModelCall, useSpanRow } from "./game-data";
import { JsonCode } from "./JsonCode";
import {
  cacheReadTokensOf,
  failed as callFailed,
  firstChunkAt,
  hostOf,
  modelOf,
  routeFact,
  violated,
} from "./model-call";
import { Picker } from "./Picker";
import { cacheableTokens, promptPartTokens, useTokenCounter } from "./prompt-tokens";

export type InspectorTarget =
  | { kind: "modelCall"; call: AiCall; activity?: Activity }
  | { kind: "tool"; span: GameSpan; activity: Activity }
  | { kind: "span"; span: GameSpan };

/** A drawer that shows one model call, tool call or span in full. */
export function Inspector({
  gameId,
  spans,
  target,
  onInspect,
  onClose,
}: {
  gameId: string;
  /** Every span the game recorded, for finding a span's parent and children. */
  spans: readonly GameSpan[];
  target: InspectorTarget | null;
  onInspect: (target: InspectorTarget) => void;
  onClose: () => void;
}) {
  const [raw, setRaw] = useState(false);
  return (
    <EdgePanel
      open={!!target}
      onOpenChange={open => {
        if (!open) onClose();
      }}
      title="Inspector"
      className="inset-x-0 top-16 bottom-0 rounded-t-3xl sm:inset-x-auto sm:top-2 sm:right-2 sm:bottom-2 sm:w-[min(46rem,calc(100vw-1rem))] sm:rounded-3xl"
    >
      <div className="absolute top-4 right-4 z-10 flex items-center gap-1">
        <Picker
          options={VIEWS}
          value={raw}
          onChange={setRaw}
          label="How to show it"
          align="right"
        />
        <button type="button" aria-label="Close" onClick={onClose} className={markButton}>
          <X className="size-4" />
        </button>
      </div>
      <div className="scroll-fade min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-6 pb-10 text-sm">
        {target && raw && <RawView gameId={gameId} target={target} />}
        {target?.kind === "modelCall" && !raw && (
          <ModelCallView call={target.call} activity={target.activity} onInspect={onInspect} />
        )}
        {target?.kind === "tool" && !raw && (
          <ToolView
            span={target.span}
            spans={spans}
            activity={target.activity}
            onInspect={onInspect}
          />
        )}
        {target?.kind === "span" && !raw && (
          <SpanView span={target.span} spans={spans} onInspect={onInspect} />
        )}
      </div>
    </EdgePanel>
  );
}

const VIEWS: { value: boolean; label: string }[] = [
  { value: false, label: "Rendered" },
  { value: true, label: "Raw" },
];

/** Exactly what was recorded, unread: the span, the stored row and both bodies as they arrived. */
function RawView({ gameId, target }: { gameId: string; target: InspectorTarget }) {
  const [above, title] =
    target.kind === "modelCall"
      ? ["Raw model call", modelOf(target.call)]
      : ["Raw span", target.span.name];
  return (
    <>
      <Title above={above} title={title} />
      <div className="mt-6">
        {target.kind === "modelCall" ? (
          <RawModelCall gameId={gameId} call={target.call} />
        ) : (
          <StoredSpan gameId={gameId} spanId={target.span.spanId} />
        )}
      </div>
    </>
  );
}

/** The span's row in `otel_traces`, every column as stored, read when it is first shown. */
function StoredSpan({ gameId, spanId }: { gameId: string; spanId: string }) {
  const row = useSpanRow(gameId, spanId);
  return (
    <>
      <Failure error={row.error} />
      {row.isPending && <p className="text-muted-foreground">Reading the span…</p>}
      {row.data === null && <p className="text-muted-foreground">Span not retained.</p>}
      {row.data && <RawSection label="Span, as otel_traces stores it" value={row.data} />}
    </>
  );
}

function RawModelCall({ gameId, call }: { gameId: string; call: AiCall }) {
  const detail = useModelCall(call.callId);
  const { requestBody, responseBody, ...row }: Partial<AiCallDetail> & AiCall = detail.data ?? call;
  const events = responseBody === undefined ? [] : jsonObjects(responseBody);
  return (
    <div className="space-y-7">
      <StoredSpan gameId={gameId} spanId={call.spanId} />
      <RawSection label="Model call row" value={row} />
      <Failure error={detail.error} />
      {detail.isPending && <p className="text-muted-foreground">Reading the call…</p>}
      {detail.data === null && <p className="text-muted-foreground">Bodies not retained.</p>}
      {requestBody !== undefined && (
        <RawBody label="Request body" body={requestBody} value={parsedOrText(requestBody)} />
      )}
      {responseBody !== undefined && (
        <RawBody
          label={
            events.length > 1 ? `Response body, ${plural(events.length, "event")}` : "Response body"
          }
          body={responseBody}
          value={events.length === 1 ? events[0] : events.length ? events : responseBody}
        />
      )}
    </div>
  );
}

function RawSection({ label, value }: { label: string; value: unknown }) {
  const text = JSON.stringify(value, null, 2);
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{label}</p>
        <CopyButton text={text} label={`Copy the ${label.toLowerCase()}`} copied="Copied" />
      </div>
      <JsonCode value={value} />
    </section>
  );
}

/** A body can run to hundreds of thousands of characters, so it opens on request. */
function RawBody({ label, body, value }: { label: string; body: string; value: unknown }) {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {label} · {formatCount(body.length)} chars
        </p>
        <CopyButton text={body} label={`Copy the ${label.toLowerCase()} as sent`} copied="Copied" />
      </div>
      <Disclosure summary={<span className="font-medium">Show it</span>}>
        {() => <JsonCode value={value} />}
      </Disclosure>
    </section>
  );
}

function parsedOrText(body: string): unknown {
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
}

function Title({
  above,
  title,
  children,
}: {
  above: ReactNode;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="pr-28">
      <p className="text-xs text-muted-foreground">{above}</p>
      <p className="mt-0.5 text-xl font-semibold tracking-tight break-all">{title}</p>
      {children && <div className="mt-1 text-xs text-muted-foreground">{children}</div>}
    </div>
  );
}

export function Facts({ facts }: { facts: [ReactNode, ReactNode][] }) {
  return (
    <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
      {facts.map(([name, value], index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: facts are a fixed list, in a fixed order
        <div key={index} className="group/fact">
          <dt className="text-xs text-muted-foreground">{name}</dt>
          <dd className="tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Block({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="mt-7">
      <p className="mb-2 text-xs text-muted-foreground">{label}</p>
      {children}
    </section>
  );
}

const msBetween = (from: string, to: string) => Date.parse(to) - Date.parse(from);

const malformedMeanings: Record<string, string> = {
  invalid_json: "The reply wasn't valid JSON.",
  schema_violation: "The reply didn't match the schema it was asked for.",
  unknown_tool: "The reply called a tool it wasn't offered.",
};

/** Where a call ran and at what tier, saying when the tier is only what was asked for. */
function factText(host: string | null, tier: { value: string; reported: boolean } | null): string {
  return [host ?? "Not reported", tier && `${tier.value} tier${tier.reported ? "" : ", as asked"}`]
    .filter(Boolean)
    .join(" · ");
}

const usdOrDash = (usd: number | null) => (usd === null ? "—" : formatUsd(usd));

/** The SKU the call was priced at, each meter it used, and what was predicted before it was sent. */
function CallPricing({ call }: { call: AiCall }) {
  const { prediction } = call;
  return (
    <Block label="Pricing">
      <Facts
        facts={[
          ["SKU", call.sku ?? "None Pax has"],
          ["Predicted from", prediction.basis],
          ["Predicted p50", usdOrDash(prediction.p50CostUsd)],
          ["Predicted p99", usdOrDash(prediction.p99CostUsd)],
          [
            "Ceiling",
            prediction.ceilingCostUsd === null
              ? "—"
              : `${formatUsd(prediction.ceilingCostUsd)}${prediction.ceilingSource ? `, ${prediction.ceilingSource}` : ""}`,
          ],
          ["Reserved", prediction.reservedFrom ?? "No hold"],
        ]}
      />
      <div className="mt-4 overflow-x-auto rounded-xl bg-foreground/[0.04] px-3 py-2 text-xs">
        <table className="w-full tabular-nums">
          <thead className="text-muted-foreground">
            <tr>
              <th className="py-1 text-left font-normal">Meter</th>
              <th className="py-1 text-right font-normal">Quantity</th>
              <th className="py-1 text-right font-normal">Per unit</th>
              <th className="py-1 text-right font-normal">Cost</th>
            </tr>
          </thead>
          <tbody>
            {call.costLines.map(line => (
              <tr key={line.meter}>
                <td className="py-1">{line.meter}</td>
                <td className="py-1 text-right">
                  {line.quantity === null ? "Unknown" : formatCount(line.quantity)}
                  <span className="text-muted-foreground"> {line.quantitySource}</span>
                </td>
                <td className="py-1 text-right">
                  {line.unitPriceUsd === null ? "Unpriced" : formatUsd(line.unitPriceUsd)}
                </td>
                <td className="py-1 text-right">{usdOrDash(line.listCostUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Block>
  );
}

/** Where the call was sent and with which headers, as the caller sent it and as Pax forwarded it. */
function CallRequest({ detail }: { detail: AiCallDetail }) {
  return (
    <Block label="Request">
      <Facts
        facts={[
          ["Sent to Pax at", detail.requestUrl],
          ["Forwarded to", detail.upstreamUrl],
        ]}
      />
      <p className="mt-4 mb-2 text-xs text-muted-foreground">Headers the caller sent</p>
      <JsonCode value={detail.requestHeaders} />
      <p className="mt-4 mb-2 text-xs text-muted-foreground">Headers Pax forwarded</p>
      <JsonCode value={detail.upstreamRequestHeaders} />
    </Block>
  );
}

/** Why a call failed or came back malformed, in full, above everything else about it. */
function CallProblem({ call, responseBody }: { call: AiCall; responseBody?: string }) {
  const failed = callFailed(call);
  if (!failed && !violated(call)) return null;
  const message = call.errorMessage;
  const events = failed && responseBody ? jsonObjects(responseBody) : [];
  const facts: [string, string][] = failed
    ? [
        ["Came from", call.errorOrigin ?? "Unknown"],
        ["Provider's code", call.errorCode ?? "None"],
        ["Ran on", hostOf(call) ?? "Not reported"],
      ]
    : [];
  return (
    <section
      className={`mt-5 rounded-2xl px-4 py-3.5 ring-1 ring-inset ${
        failed
          ? "bg-destructive/[0.06] ring-destructive/20"
          : "bg-amber-500/[0.07] ring-amber-500/25"
      }`}
    >
      <p
        className={`font-medium ${failed ? "text-destructive" : "text-amber-700 dark:text-amber-400"}`}
      >
        {failed ? "Failed" : "Malformed"} · {call.errorType ?? "error"}
      </p>
      {!failed && call.errorType && malformedMeanings[call.errorType] && (
        <p className="mt-0.5 text-muted-foreground">{malformedMeanings[call.errorType]}</p>
      )}
      {message && (
        <div className="mt-3 flex items-start gap-2">
          <p className="min-w-0 flex-1 font-mono text-xs leading-5 break-words whitespace-pre-wrap">
            {message}
          </p>
          <CopyButton text={message} label="Copy the error message" copied="Copied" />
        </div>
      )}
      {facts.length > 0 && (
        <dl className="mt-3 grid grid-cols-3 gap-x-6">
          {facts.map(([name, value]) => (
            <div key={name}>
              <dt className="text-xs text-muted-foreground">{name}</dt>
              <dd className="break-all">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {responseBody && failed && (
        <div className="mt-3">
          <Disclosure summary={<span className="font-medium">What the provider sent back</span>}>
            {() => (
              <JsonCode
                value={events.length === 1 ? events[0] : events.length ? events : responseBody}
              />
            )}
          </Disclosure>
        </div>
      )}
    </section>
  );
}

/** The call's prompt and what the model sent back, read from its exact bodies. */
function readCall({ transport, requestBody, responseBody }: AiCallDetail) {
  const spoken = ModelTransport.parse(transport);
  return {
    prompt: readModelPrompt(spoken, requestBody),
    reply: readModelReply(spoken, responseBody),
    chunks: readModelChunks(spoken, responseBody),
    rawChunks: jsonObjects(responseBody),
  };
}

export function ModelCallView({
  call,
  activity,
  onInspect,
}: {
  call: AiCall;
  activity?: Activity;
  onInspect: (target: InspectorTarget) => void;
}) {
  const position = activity ? activity.modelCalls.indexOf(call) : -1;
  const next = activity && position >= 0 ? activity.modelCalls[position + 1] : undefined;
  const detail = useModelCall(call.callId);
  const source = detail.data?.cacheSource ?? null;
  const sourceDetail = useModelCall(source?.callId);
  const read = detail.data ? readCall(detail.data) : null;
  const sourceRead = sourceDetail.data ? readCall(sourceDetail.data) : null;
  const firstChunk = firstChunkAt(call);
  const tools = activity ? toolSpansAfter(activity, call) : [];
  const toolMs = tools.reduce((sum, span) => sum + span.durationMs, 0);
  const afterCall: [string, string] = tools.length
    ? ["Tools then ran for", formatDuration(toolMs)]
    : next
      ? ["Until next model call", formatDuration(msBetween(call.endedAt, next.receivedAt))]
      : ["Tools then ran for", read?.reply.toolCalls.length ? "—" : "No tools"];

  return (
    <>
      <Title
        above={`Model call${position >= 0 ? ` ${position + 1} of ${activity?.modelCalls.length}` : ""}`}
        title={modelOf(call)}
      >
        {call.transport} · <Timestamp at={call.receivedAt} />
      </Title>
      <CallProblem call={call} responseBody={detail.data?.responseBody} />
      <Facts
        facts={[
          [
            "First chunk after",
            firstChunk ? formatDuration(msBetween(call.receivedAt, firstChunk)) : "—",
          ],
          ["Model time", formatDuration(msBetween(call.receivedAt, call.endedAt))],
          afterCall,
          ["Input tokens", call.inputTokens.toLocaleString()],
          ["Cache read", cacheReadTokensOf(call).toLocaleString()],
          ["Cache write", call.cacheWriteTokens.toLocaleString()],
          [
            "Reasoning tokens",
            call.reasoningTokens === null ? "Not reported" : call.reasoningTokens.toLocaleString(),
          ],
          ["Output tokens", call.outputTokens.toLocaleString()],
          ["Billed", call.billedCostUsd === null ? "Not reported" : formatUsd(call.billedCostUsd)],
          ["List cost", call.listCostUsd === null ? "Unknown" : formatUsd(call.listCostUsd)],
          ["Ran on", factText(hostOf(call), routeFact(call, "tier"))],
          [
            "Provider's finish",
            call.nativeFinishReasons.length ? call.nativeFinishReasons.join(", ") : "Not reported",
          ],
          ...(activity ? [newlySeenFact(firstSeenIn(activity, call))] : []),
        ]}
      />
      <CallPricing call={call} />
      {detail.data && <CallRequest detail={detail.data} />}
      <Failure error={detail.error} />
      {detail.data === null && (
        <p className="mt-7 rounded-2xl bg-foreground/5 px-4 py-3 text-muted-foreground">
          Prompt not retained. The numbers above come from the call's span.
        </p>
      )}
      {detail.isPending && <p className="mt-7 text-muted-foreground">Reading the call…</p>}
      {read && (
        <>
          <CallTimeline
            call={call}
            chunks={read.chunks}
            chunkOffsetsMs={detail.data?.eventOffsetsMs ?? []}
            rawChunks={read.rawChunks}
            reasoningHidden={read.reply.reasoningEncryptedWithoutSummary}
          />
          {read.reply.reasoning && (
            <Block label="Reasoning summary">
              <p className="leading-6 whitespace-pre-wrap text-muted-foreground">
                {read.reply.reasoning}
              </p>
            </Block>
          )}
          {read.reply.reasoningEncryptedWithoutSummary && (
            <Block label="Reasoning">
              <p className="text-muted-foreground">Encrypted reasoning, no summary</p>
            </Block>
          )}
          {read.reply.text && (
            <Block label="Output">
              <p className="leading-6 whitespace-pre-wrap">{read.reply.text}</p>
            </Block>
          )}
          {read.reply.toolCalls.length > 0 && (
            <Block label="Tool calls">
              {read.reply.toolCalls.map((toolCall, index) => {
                const span = tools.find(tool => toolCallIdOf(tool) === toolCall.id);
                const result = span && toolResultOf(span);
                return (
                  <Disclosure
                    // biome-ignore lint/suspicious/noArrayIndexKey: tool calls keep their order
                    key={index}
                    initiallyOpen={read.reply.toolCalls.length === 1}
                    summary={
                      <span className="flex justify-between gap-4">
                        <span className="font-medium">{toolCall.name}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {span ? `ran ${formatDuration(span.durationMs)}` : ""}
                        </span>
                      </span>
                    }
                  >
                    {() => (
                      <div className="space-y-2">
                        <ReadableValue value={toolCall.arguments} />
                        {result !== undefined ? (
                          <>
                            <p className="text-xs text-muted-foreground">Result</p>
                            <ReadableValue value={result} />
                          </>
                        ) : (
                          <p className="text-xs text-muted-foreground">No result recorded.</p>
                        )}
                        {span && activity && (
                          <button
                            type="button"
                            className={`text-xs text-muted-foreground ${link}`}
                            onClick={() => onInspect({ kind: "tool", span, activity })}
                          >
                            Open the tool span
                          </button>
                        )}
                      </div>
                    )}
                  </Disclosure>
                );
              })}
            </Block>
          )}
          <Prompt
            call={call}
            parts={read.prompt.parts}
            source={source}
            sourceParts={sourceRead?.prompt.parts}
            sourceCall={sourceDetail.data ?? undefined}
            earlierReasoning={detail.data?.earlierReasoning ?? []}
            onInspect={onInspect}
          />
        </>
      )}
    </>
  );
}

const newlySeenFact = (revisions: number[]): [string, string] => [
  "Deltas first seen",
  revisions.length ? revisionList(revisions) : "None",
];

/**
 * The whole prompt, with the stretch it shares with the best earlier call under its cache key
 * marked: what the provider could have read from its cache, beside what it reported reading.
 */
function Prompt({
  call,
  parts,
  source,
  sourceParts,
  sourceCall,
  earlierReasoning,
  onInspect,
}: {
  call: AiCall;
  parts: PromptPart[];
  source: AiCallDetail["cacheSource"];
  sourceParts?: PromptPart[];
  sourceCall?: AiCall;
  earlierReasoning: AiCallDetail["earlierReasoning"];
  onInspect: (target: InspectorTarget) => void;
}) {
  const count = useTokenCounter();
  const tokens = count && promptPartTokens(parts, count, call.inputTokens, earlierReasoning);
  const prefix = sourceParts
    ? sharedPromptPrefix(sourceParts, parts)
    : { wholeParts: 0, characters: 0 };
  const splitPart = parts[prefix.wholeParts];
  const shared = tokens
    ? tokens.parts.slice(0, prefix.wholeParts).reduce((sum, part) => sum + part.tokens, 0) +
      Math.round(
        ((tokens.parts[prefix.wholeParts]?.tokens ?? 0) * prefix.characters) /
          Math.max(1, splitPart?.text.length ?? 1),
      )
    : null;
  const ideal = shared === null ? null : cacheableTokens(shared);
  const observed = cacheReadTokensOf(call);
  const share = (tokens: number) =>
    call.inputTokens ? `${Math.round((tokens / call.inputTokens) * 100)}%` : "0%";
  return (
    <Block label="Prompt">
      <Facts
        facts={[
          [
            "Could have read from cache",
            ideal === null ? "Counting…" : `${formatCount(ideal)} (${share(ideal)})`,
          ],
          ["Read from cache", `${formatCount(observed)} (${share(observed)})`],
          [
            "Shares its start with",
            source && sourceCall ? (
              <button
                key="source"
                type="button"
                className={link}
                onClick={() => onInspect({ kind: "modelCall", call: sourceCall })}
              >
                A call {formatDuration(msBetween(source.endedAt, call.receivedAt))} earlier
              </button>
            ) : source ? (
              "Reading…"
            ) : (
              "No earlier call"
            ),
          ],
          ...(tokens && tokens.framing !== 0
            ? [
                ["Framing around parts", `${formatCount(tokens.framing)} tokens`] as [
                  string,
                  string,
                ],
              ]
            : []),
        ]}
      />
      <div className="mt-4 space-y-1">
        {parts.map((part, index) => {
          const whole = index < prefix.wholeParts;
          const split = index === prefix.wholeParts && prefix.characters > 0;
          return (
            <Disclosure
              // biome-ignore lint/suspicious/noArrayIndexKey: prompt parts keep their order
              key={index}
              summary={
                <span className="flex items-center gap-2">
                  <span
                    className={`size-1.5 shrink-0 rounded-full ${whole ? "bg-emerald-500" : split ? "bg-emerald-500/50" : "bg-foreground/20"}`}
                  />
                  <span className="shrink-0 font-medium">{part.label}</span>
                  <span className="min-w-0 truncate text-xs text-muted-foreground">
                    {part.text.slice(0, 160)}
                  </span>
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
                    {tokens?.parts[index]
                      ? `${tokens.parts[index].derived ? "≈" : ""}${formatCount(tokens.parts[index].tokens)} tokens`
                      : part.sent
                        ? `${formatCount(part.sent.length)} chars`
                        : ""}
                  </span>
                </span>
              }
            >
              {() => (
                <pre className="max-h-[32rem] overflow-auto rounded-xl bg-foreground/5 p-3 font-mono text-xs leading-5 break-words whitespace-pre-wrap">
                  {whole ? (
                    <mark className="bg-emerald-500/15 text-inherit">{part.text}</mark>
                  ) : split ? (
                    <>
                      <mark className="bg-emerald-500/15 text-inherit">
                        {part.text.slice(0, prefix.characters)}
                      </mark>
                      {part.text.slice(prefix.characters)}
                    </>
                  ) : (
                    part.text
                  )}
                </pre>
              )}
            </Disclosure>
          );
        })}
      </div>
    </Block>
  );
}

function ToolView({
  span,
  spans,
  activity,
  onInspect,
}: {
  span: GameSpan;
  spans: readonly GameSpan[];
  activity: Activity;
  onInspect: (target: InspectorTarget) => void;
}) {
  const call = modelCallBefore(activity, span);
  const next = call ? activity.modelCalls[activity.modelCalls.indexOf(call) + 1] : undefined;
  const detail = useModelCall(call?.callId);
  const name = toolNameOf(span);
  const asked = detail.data
    ? readModelReply(ModelTransport.parse(detail.data.transport), detail.data.responseBody)
    : null;
  const toolCall = asked?.toolCalls.find(candidate => candidate.id === toolCallIdOf(span));
  const result = toolResultOf(span);
  return (
    <>
      <Title above="Tool call" title={name}>
        {span.service} · <Timestamp at={span.startedAt} />
        {span.status === "error" && ` · failed: ${span.statusMessage ?? "error"}`}
      </Title>
      <Facts
        facts={[
          ["Ran for", formatDuration(span.durationMs)],
          [
            "Result reached the model after",
            next ? formatDuration(Date.parse(next.receivedAt) - Date.parse(span.startedAt)) : "—",
          ],
          ["Status", span.status],
          parentFact(span, spans, onInspect),
        ]}
      />
      {call && (
        <p className="mt-4 text-xs text-muted-foreground">
          Asked for by{" "}
          <button
            type="button"
            className={link}
            onClick={() => onInspect({ kind: "modelCall", call, activity })}
          >
            model call {activity.modelCalls.indexOf(call) + 1}
          </button>
          {next && (
            <>
              ; its result went into{" "}
              <button
                type="button"
                className={link}
                onClick={() => onInspect({ kind: "modelCall", call: next, activity })}
              >
                model call {activity.modelCalls.indexOf(next) + 1}
              </button>
            </>
          )}
          .
        </p>
      )}
      {detail.data === null && (
        <p className="mt-7 rounded-2xl bg-foreground/5 px-4 py-3 text-muted-foreground">
          Prompt not retained, so the arguments are unknown.
        </p>
      )}
      {toolCall && (
        <Block label="Arguments">
          <ReadableValue value={toolCall.arguments} />
        </Block>
      )}
      {result !== undefined && (
        <Block label="Result">
          <ReadableValue value={result} />
        </Block>
      )}
      <SpanRecord span={span} spans={spans} onInspect={onInspect} />
    </>
  );
}

/** Where a span sits in its trace: the span it ran under, and the spans that ran under it. */
function Lineage({
  span,
  spans,
  onInspect,
}: {
  span: GameSpan;
  spans: readonly GameSpan[];
  onInspect: (target: InspectorTarget) => void;
}) {
  const children = spans.filter(
    candidate => candidate.traceId === span.traceId && candidate.parentSpanId === span.spanId,
  );
  if (children.length === 0) return null;
  return (
    <Block label={`Runs ${plural(children.length, "span")} under it`}>
      <ul className="space-y-0.5">
        {children.map(child => (
          <li key={child.spanId}>
            <button
              type="button"
              onClick={() => onInspect({ kind: "span", span: child })}
              className={`-mx-2 flex w-[calc(100%+1rem)] items-center justify-between gap-3 rounded-lg px-2 py-1 text-left hover:bg-foreground/5 ${panelMotion} ${focusHaloFlush}`}
            >
              <span className="truncate">{child.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {formatDuration(child.durationMs)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Block>
  );
}

/** The span this one ran under, as a link, or word that it began its trace. */
function parentFact(
  span: GameSpan,
  spans: readonly GameSpan[],
  onInspect: (target: InspectorTarget) => void,
): [string, ReactNode] {
  if (!span.parentSpanId) return ["Parent", "None, it starts its trace"];
  const parent = spans.find(
    candidate => candidate.traceId === span.traceId && candidate.spanId === span.parentSpanId,
  );
  if (!parent) return ["Parent", <CopyableId key="parent" value={span.parentSpanId} />];
  return [
    "Parent",
    <button
      key="parent"
      type="button"
      className={`max-w-full truncate text-left ${link}`}
      onClick={() => onInspect({ kind: "span", span: parent })}
    >
      {parent.name}
    </button>,
  ];
}

function SpanView({
  span,
  spans,
  onInspect,
}: {
  span: GameSpan;
  spans: readonly GameSpan[];
  onInspect: (target: InspectorTarget) => void;
}) {
  return (
    <>
      <Title
        above={`${span.service} · ${span.parentSpanId ? "child span" : "root span"}`}
        title={span.name}
      >
        <Timestamp at={span.startedAt} /> to <Timestamp at={spanEndMs(span)} dateless />
        {span.status === "error" && ` · failed: ${span.statusMessage ?? "error"}`}
      </Title>
      <Facts
        facts={[
          ["Took", formatDuration(span.durationMs)],
          ["Status", span.status],
          parentFact(span, spans, onInspect),
        ]}
      />
      <Lineage span={span} spans={spans} onInspect={onInspect} />
      {span.attributes[GEN_AI.operationName] === "chat" && (
        <p className="mt-7 rounded-2xl bg-foreground/5 px-4 py-3 text-muted-foreground">
          Prompt not retained. The span below is all that was recorded of this call.
        </p>
      )}
      <SpanRecord span={span} spans={spans} onInspect={onInspect} />
    </>
  );
}

/** Everything a span recorded beyond its timing: its moments, links, attributes and process. */
function SpanRecord({
  span,
  spans,
  onInspect,
}: {
  span: GameSpan;
  spans: readonly GameSpan[];
  onInspect: (target: InspectorTarget) => void;
}) {
  return (
    <>
      {span.events.length > 0 && (
        <Block label="Events">
          <ul className="space-y-3">
            {span.events.map((event, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: events keep their order
              <li key={index}>
                <p className="flex justify-between gap-4">
                  <span className="font-medium">{event.name}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    +{formatDuration(Date.parse(event.at) - Date.parse(span.startedAt))}
                  </span>
                </p>
                {Object.keys(event.attributes).length > 0 && (
                  <div className="mt-1.5">
                    <Attributes attributes={event.attributes} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Block>
      )}
      {span.links.length > 0 && (
        <Block label="Follows from">
          <ul className="space-y-1 font-mono text-xs">
            {span.links.map(linked => {
              const target = spans.find(candidate => candidate.spanId === linked.spanId);
              return (
                <li key={`${linked.traceId}-${linked.spanId}`}>
                  {target ? (
                    <button
                      type="button"
                      className={link}
                      onClick={() => onInspect({ kind: "span", span: target })}
                    >
                      {target.service} · {target.name}
                    </button>
                  ) : (
                    <span className="text-muted-foreground">
                      trace {linked.traceId}, span {linked.spanId}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </Block>
      )}
      <Block label="Attributes">
        <Attributes attributes={span.attributes} />
      </Block>
      <Block label={`Process · ${span.kind} span from ${span.scope || "an unnamed library"}`}>
        <Attributes attributes={span.resource} />
      </Block>
    </>
  );
}

function Attributes({ attributes }: { attributes: Record<string, string> }) {
  const entries = Object.entries(attributes).sort(([left], [right]) => left.localeCompare(right));
  return (
    <dl className="space-y-1.5 font-mono text-xs">
      {entries.map(([key, value]) => (
        <div key={key} className="grid grid-cols-[minmax(0,14rem)_1fr] gap-3">
          <dt className="truncate text-muted-foreground" title={key}>
            {key}
          </dt>
          <dd className="break-all whitespace-pre-wrap">{value}</dd>
        </div>
      ))}
      {entries.length === 0 && <p className="text-muted-foreground">No attributes.</p>}
    </dl>
  );
}
